if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.nfaReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.nfaLastMedia = this.elements.viewer?.querySelector('.product__media-item.is-active') ||
          this.elements.viewer?.querySelector('.product__media-item[data-media-id]');

        // NFA: add a quick card-like settling motion whenever the product gallery changes.
        // This listener is intentionally registered even when thumbnails are disabled,
        // so mobile swipe gestures get the same effect as arrow/thumbnail navigation.
        this.elements.viewer?.addEventListener('slideChanged', this.onNfaSlideChanged.bind(this));

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onNfaSlideChanged(event) {
        this.animateNfaMediaChange(event.detail.currentElement);
      }

      animateNfaMediaChange(activeMedia) {
        if (!activeMedia || activeMedia === this.nfaLastMedia) return;

        const previousMedia = this.nfaLastMedia;
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const previousIndex = previousMedia ? mediaItems.indexOf(previousMedia) : -1;
        const activeIndex = mediaItems.indexOf(activeMedia);
        const direction = previousIndex === -1 || activeIndex >= previousIndex ? 1 : -1;

        this.nfaLastMedia = activeMedia;

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.toggle('is-active', item === activeMedia);
        });

        if (this.nfaReduceMotion.matches || typeof activeMedia.animate !== 'function') return;

        const activeSurface = activeMedia.querySelector('.product-media-container') || activeMedia;
        const previousSurface = previousMedia?.querySelector('.product-media-container') || previousMedia;

        activeSurface.getAnimations().forEach((animation) => animation.cancel());
        previousSurface?.getAnimations().forEach((animation) => animation.cancel());

        // Fast "Safari tab card" feel: a slight side flick + scale/tilt, then a crisp settle.
        activeSurface.animate(
          [
            {
              transform: `translate3d(${direction * 24}px, 0, 0) scale(.965) rotate(${direction * 0.55}deg)`,
              opacity: 0.82,
            },
            {
              offset: 0.68,
              transform: `translate3d(${-direction * 2}px, 0, 0) scale(1.006) rotate(${-direction * 0.06}deg)`,
              opacity: 1,
            },
            {
              transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)',
              opacity: 1,
            },
          ],
          {
            duration: 240,
            easing: 'cubic-bezier(.18,.88,.22,1)',
            fill: 'both',
          }
        );

        if (previousSurface) {
          previousSurface.animate(
            [
              { transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)', opacity: 1 },
              {
                transform: `translate3d(${-direction * 16}px, -2px, 0) scale(.975) rotate(${-direction * 0.35}deg)`,
                opacity: 0.72,
              },
            ],
            {
              duration: 145,
              easing: 'cubic-bezier(.4,0,1,1)',
            }
          );
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.animateNfaMediaChange(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
